import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Heart, Trash2, Flag } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { resolveEatzyMediaUrl } from "@/lib/utils";
import { AuthenticatedAvatarImage } from "@/components/AuthenticatedAvatarImage";
import TrustBadge, { getTrustTier } from "./TrustBadge";
import { useToast } from "@/hooks/use-toast";
import { ReportModal } from "./ReportModal";
import type { Comment } from "@shared/schema";

// Enriched comment type from API
interface EnrichedComment extends Comment {
  authorName: string;
  authorAvatar: string | null;
  authorTrustScore: number;
  isOwnComment: boolean;
  likedByCurrentUser: boolean;
}

interface CommentsSheetProps {
  reviewId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function CommentsSheet({ reviewId, isOpen, onClose }: CommentsSheetProps) {
  const [commentText, setCommentText] = useState("");
  const [likedComments, setLikedComments] = useState<Set<string>>(new Set());
  const [reportingCommentId, setReportingCommentId] = useState<string | null>(null);
  const { toast } = useToast();

  const { data: comments = [], isLoading } = useQuery<EnrichedComment[]>({
    queryKey: ['/api/reviews', reviewId, 'comments'],
    enabled: isOpen && !!reviewId,
  });

  const createCommentMutation = useMutation({
    mutationFn: async (text: string) => {
      return apiRequest("POST", `/api/reviews/${reviewId}/comments`, { text });
    },
    onSuccess: () => {
      setCommentText("");
      queryClient.invalidateQueries({ queryKey: ['/api/reviews', reviewId, 'comments'] });
      queryClient.invalidateQueries({ queryKey: ['/api/reviews'] });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      return apiRequest("DELETE", `/api/comments/${commentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/reviews', reviewId, 'comments'] });
      queryClient.invalidateQueries({ queryKey: ['/api/reviews'] });
      toast({ title: "Comment deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete comment", variant: "destructive" });
    },
  });

  const likeCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      return apiRequest("POST", `/api/comments/${commentId}/like`);
    },
    onSuccess: (_, commentId) => {
      setLikedComments(prev => {
        const next = new Set(prev);
        if (next.has(commentId)) {
          next.delete(commentId);
        } else {
          next.add(commentId);
        }
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['/api/reviews', reviewId, 'comments'] });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (commentText.trim()) {
      createCommentMutation.mutate(commentText);
    }
  };

  const handleLike = (commentId: string) => {
    likeCommentMutation.mutate(commentId);
  };

  const handleDelete = (commentId: string) => {
    if (confirm("Delete this comment?")) {
      deleteCommentMutation.mutate(commentId);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="bottom" className="h-[85svh] flex flex-col p-0">
          <SheetHeader className="p-4 border-b">
            <SheetTitle className="text-lg">
              Comments {comments.length > 0 && `(${comments.length})`}
            </SheetTitle>
          </SheetHeader>

          <ScrollArea className="flex-1 px-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-muted-foreground">Loading comments...</div>
              </div>
            ) : comments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  No comments yet
                </p>
                <p className="text-xs text-muted-foreground">
                  Be the first to share your thoughts!
                </p>
              </div>
            ) : (
              <div className="space-y-4 py-4">
                {comments.map((comment) => {
                  // Use API's likedByCurrentUser as the source of truth
                  // Local likedComments is only for optimistic updates
                  const isLiked = comment.likedByCurrentUser || likedComments.has(comment.id);
                  const trustTier = getTrustTier(comment.authorTrustScore || 0);
                  
                  return (
                    <div
                      key={comment.id}
                      className="flex gap-3"
                      data-testid={`comment-${comment.id}`}
                    >
                      <Avatar className="w-8 h-8 flex-shrink-0">
                        <AuthenticatedAvatarImage src={resolveEatzyMediaUrl(comment.authorAvatar) || ""} />
                        <AvatarFallback className="text-xs bg-kudoz text-white">
                          {getInitials(comment.authorName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span
                            className="text-sm font-semibold"
                            data-testid={`comment-author-${comment.id}`}
                          >
                            {comment.authorName}
                          </span>
                          {comment.authorTrustScore > 0 && (
                            <TrustBadge
                              trustScore={comment.authorTrustScore}
                              trustTier={trustTier}
                              size="xs"
                              showScore={false}
                              showLabel={false}
                            />
                          )}
                          <span className="text-xs text-muted-foreground">
                            {comment.createdAt && formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                          </span>
                        </div>
                        <p
                          className="text-sm break-words mb-1.5"
                          data-testid={`comment-text-${comment.id}`}
                        >
                          {comment.text}
                        </p>
                        {/* Comment actions */}
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleLike(comment.id)}
                            className="h-7 px-2 text-xs gap-1"
                            data-testid={`button-like-comment-${comment.id}`}
                          >
                            <Heart className={`w-3.5 h-3.5 ${isLiked ? "fill-red-500 text-red-500" : ""}`} />
                            <span>{comment.likes || 0}</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setReportingCommentId(comment.id)}
                            className="h-7 px-2 text-xs text-muted-foreground"
                            data-testid={`button-report-comment-${comment.id}`}
                          >
                            <Flag className="w-3.5 h-3.5" />
                          </Button>
                          {comment.isOwnComment && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(comment.id)}
                              className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                              disabled={deleteCommentMutation.isPending}
                              data-testid={`button-delete-comment-${comment.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          <div className="p-4 border-t bg-background">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <Textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Add a comment..."
                className="resize-none min-h-[44px] max-h-[120px]"
                rows={1}
                data-testid="input-comment"
              />
              <Button
                type="submit"
                size="icon"
                disabled={!commentText.trim() || createCommentMutation.isPending}
                data-testid="button-send-comment"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </SheetContent>
      </Sheet>

      {/* Report Modal */}
      <ReportModal
        isOpen={!!reportingCommentId}
        onClose={() => setReportingCommentId(null)}
        targetType="comment"
        targetId={reportingCommentId || ""}
      />
    </>
  );
}
